import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { KitchenStatus, KitchenTicket } from '@popcart/core/v1';
import { api } from '../../api';
import { ExpressChip, Loading, StatusPill, useNow, useTitle } from '../../ui/bits';
import { Fig } from '../../ui/viz';
import { waitText } from '../StatusSticker';
import { Scene, SceneError } from '../../catalog/art';
import { Icon } from '../../ui/Icon';
import { usePrefs } from '../../ui/prefs';
import { useToast } from '../../ui/toast';
import { playNewOrderTone } from '../../ui/sound';
import { QUEUE_MS, shopKeys, useShop } from '../ShopLayout';
import {
  COLUMNS,
  boardOrder,
  buildBoard,
  canStart,
  canUndo,
  chimeMessage,
  clockFor,
  detectNewTickets,
  firesAtLabel,
  moveLabel,
  moveProblem,
  moveSelection,
  nextStatus,
  openCount,
  otherLinesNote,
  replaceTicket,
  startsAtLabel,
  timerLabel,
} from './lib/board.js';
import { grillKeys } from './keys';
import { MenuPanel } from './MenuPanel';
import './grill.css';

type Seen = { active: Set<string>; later: Set<string> };
const ARROWS = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'] as const;
type Arrow = (typeof ARROWS)[number];

/** /shop/:shopId/grill — the Grill kitchen ticket board (tablet-first). */
export function GrillPage() {
  useTitle('Grill · kitchen');
  const { shopId, queue, newIds } = useShop();
  const qc = useQueryClient();
  const toast = useToast();
  const { muted } = usePrefs();
  const now = useNow(5_000);
  const [panelOpen, setPanelOpen] = useState(false);

  const kitchen = useQuery({
    queryKey: grillKeys.kitchen(shopId),
    queryFn: () => api.kitchen(shopId),
    refetchInterval: QUEUE_MS,
    refetchIntervalInBackground: true,
    enabled: !!shopId,
  });
  const tickets = useMemo(() => kitchen.data ?? [], [kitchen.data]);
  const board = useMemo(() => buildBoard(tickets, now), [tickets, now]);
  const order = useMemo(() => boardOrder(board), [board]);

  // ── New-ticket chime: once per ticket, respecting Sound on/off; the layout chimes placed orders.
  const seen = useRef<Seen | null>(null);
  const [flash, setFlash] = useState<Set<string>>(() => new Set());
  const layoutRef = useRef({ queue, newIds, muted });
  layoutRef.current = { queue, newIds, muted };
  useEffect(() => {
    seen.current = null;
    setFlash(new Set());
  }, [shopId]);
  useEffect(() => {
    if (!kitchen.data) return;
    const l = layoutRef.current;
    const { chime, seen: next } = detectNewTickets(seen.current, kitchen.data, Date.now(), {
      queueIds: new Set(l.queue.map((q) => q.shopOrderId)),
      layoutChimed: l.newIds,
    });
    seen.current = next;
    if (!chime.length) return;
    if (!l.muted) playNewOrderTone();
    const msg = chimeMessage(chime);
    if (msg) toast(msg, 'order');
    setFlash((s) => {
      const n = new Set(s);
      for (const t of chime) n.add(t.shopOrderId);
      return n;
    });
  }, [kitchen.data, now, toast]);

  // ── Selection (the selected ticket's forward step is the screen's one Volt).
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selected = order.find((t) => t.shopOrderId === selectedId) ?? order[0] ?? null;
  const boardRef = useRef<HTMLDivElement>(null);

  // ── Moves.
  const [problems, setProblems] = useState<Record<string, string>>({});
  const [announce, setAnnounce] = useState('');
  const move = useMutation({
    mutationFn: ({ t, to }: { t: KitchenTicket; to: KitchenStatus }) => api.moveTicket(shopId, t.shopOrderId, to),
    onSuccess: (updated, { t, to }) => {
      qc.setQueryData<KitchenTicket[]>(grillKeys.kitchen(shopId), (old) => replaceTicket(old ?? [], updated));
      setProblems(({ [t.shopOrderId]: _drop, ...rest }) => rest);
      const col = COLUMNS.find((c) => c.status === to)?.title ?? to;
      setAnnounce(`${t.code} moved to ${col}.`);
      void qc.invalidateQueries({ queryKey: shopKeys.queue(shopId) });
      void qc.invalidateQueries({ queryKey: shopKeys.today(shopId) });
    },
    onError: (err, { t }) => {
      const msg = moveProblem(err, t);
      setProblems((p) => ({ ...p, [t.shopOrderId]: msg }));
      setAnnounce(msg);
      if ((err as { code?: string }).code !== 'not_fired') void qc.invalidateQueries({ queryKey: grillKeys.kitchen(shopId) });
    },
  });
  const pendingId = move.isPending ? (move.variables?.t.shopOrderId ?? null) : null;
  const doMove = (t: KitchenTicket, to: KitchenStatus) => {
    if (pendingId) return;
    move.mutate({ t, to });
  };

  const focusTicket = (id: string | null) => {
    if (!id) return;
    window.requestAnimationFrame(() => boardRef.current?.querySelector<HTMLElement>(`[data-ticket="${CSS.escape(id)}"]`)?.focus());
  };
  const onBoardKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    if (!target.hasAttribute('data-ticket')) return; // buttons inside a ticket keep their own keys
    if ((ARROWS as readonly string[]).includes(e.key)) {
      e.preventDefault();
      const id = moveSelection(board, selected?.shopOrderId ?? null, e.key as Arrow);
      setSelectedId(id);
      focusTicket(id);
    } else if ((e.key === 'Enter' || e.key === ' ') && selected) {
      e.preventDefault();
      const to = nextStatus(selected.kitchenStatus);
      if (to) doMove(selected, to);
    } else if (e.key === 'Backspace' && selected && canUndo(selected.kitchenStatus)) {
      e.preventDefault();
      doMove(selected, 'queued');
    }
  };

  const cooking = openCount(tickets, now);

  return (
    <>
      <header className="dv-head">
        <div className="stack s4">
          <h1>Grill · kitchen</h1>
          <span className="dv-live" title="Express first, then oldest. Timers count down to ready.">
            <span className="dv-pulse" aria-hidden="true" />
            {kitchen.data ? `${cooking} on the board` : 'Live'}
          </span>
        </div>
        <div className="dv-actions">
          <SoundToggle />
          <button type="button" className="btn btn-quiet" aria-expanded={panelOpen} aria-haspopup="dialog" onClick={() => setPanelOpen(true)}>
            <Icon name="list" />
            Sold-out options
          </button>
        </div>
      </header>
      <p className="sr-only" role="status" aria-live="polite" aria-atomic="true">
        {announce}
      </p>

      <div className="gk">
        <div className="stack s12" style={{ minWidth: 0 }}>
          {kitchen.isError ? (
            <section className="card">
              <SceneError error={kitchen.error} onRetry={() => void kitchen.refetch()} />
            </section>
          ) : kitchen.isLoading ? (
            <Loading label="Loading the kitchen board" rows={3} />
          ) : (
            <>
              {board.later.length ? (
                <section className="card dv-card dv-rise" aria-labelledby="gk-later-h">
                  <div className="dv-card-h">
                    <h2 id="gk-later-h">Later</h2>
                    <span className="dv-chip">{board.later.length}</span>
                    <span className="grow" />
                    <span className="dv-sub" title="Scheduled. Start up to 10 min before fire time.">
                      Scheduled
                    </span>
                  </div>
                  <ul className="gk-later" aria-label="Scheduled tickets">
                    {board.later.map((t) => (
                      <li key={t.shopOrderId}>
                        <LaterTicket t={t} now={now} pending={pendingId === t.shopOrderId} problem={problems[t.shopOrderId] ?? null} onStart={() => doMove(t, 'on_grill')} />
                      </li>
                    ))}
                  </ul>
                </section>
              ) : null}

              {tickets.length === 0 ? (
                <section className="card">
                  <Scene scene="grill" title="Grill’s hot. No tickets yet.">
                    Grill orders show up here with a sound once they’re accepted. Keep this screen open on the kitchen tablet.
                  </Scene>
                </section>
              ) : (
                <div ref={boardRef} className="gk-board" role="region" aria-label="Kitchen tickets. Arrow keys pick a ticket, Enter moves it on." onKeyDown={onBoardKey}>
                  {COLUMNS.map((c) => {
                    const list = board.columns[c.status];
                    return (
                      <section key={c.status} className={`col gk-col gk-${c.status}`} aria-labelledby={`gk-col-${c.status}`}>
                        <div className="head">
                          <h2 id={`gk-col-${c.status}`}>{c.title}</h2>
                          <Fig text={String(list.length)} size="sm" />
                        </div>
                        {list.length === 0 ? (
                          <div className="empty gk-empty">Nothing here</div>
                        ) : (
                          list.map((t) => (
                            <Ticket
                              key={t.shopOrderId}
                              t={t}
                              now={now}
                              selected={t.shopOrderId === selected?.shopOrderId}
                              isNew={flash.has(t.shopOrderId)}
                              pending={pendingId === t.shopOrderId}
                              problem={problems[t.shopOrderId] ?? null}
                              onSelect={() => setSelectedId(t.shopOrderId)}
                              onMove={(to) => {
                                setSelectedId(t.shopOrderId);
                                doMove(t, to);
                              }}
                            />
                          ))
                        )}
                      </section>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
        <MenuPanel open={panelOpen} shopId={shopId} onClose={() => setPanelOpen(false)} />
      </div>
    </>
  );
}

function SoundToggle() {
  const { muted, setMuted } = usePrefs();
  return (
    <button type="button" className="dv-ibtn press" aria-pressed={!muted} aria-label={`Sound ${muted ? 'off' : 'on'}`} title={`New-ticket sound ${muted ? 'off' : 'on'}`} onClick={() => setMuted(!muted)}>
      <Icon name={muted ? 'bellOff' : 'bell'} />
    </button>
  );
}

function Timer({ t, now }: { t: KitchenTicket; now: number }) {
  if (t.kitchenStatus === 'ready') return <StatusPill tone="success">Ready</StatusPill>;
  const c = clockFor(t, now);
  const label = timerLabel(c);
  if (!label) return null;
  if (c.late)
    return (
      <span className="gk-timer late" role="img" aria-label={label}>
        <Fig text={waitText(Math.abs(c.minutesLeft ?? 0))} size="md" animate={false} />
        <small aria-hidden="true">late</small>
      </span>
    );
  if (c.minutesLeft === 0)
    return (
      <span className="gk-timer due" role="img" aria-label={`Ready by: ${label}`}>
        <b aria-hidden="true">Due now</b>
      </span>
    );
  return (
    <span className="gk-timer" role="img" aria-label={`Ready by: ${label}`}>
      <Fig text={waitText(c.minutesLeft)} size="md" animate={false} />
      <small aria-hidden="true">left</small>
    </span>
  );
}

function Items({ t }: { t: KitchenTicket }) {
  const other = otherLinesNote(t.otherLines);
  return (
    <>
      <ul className="gk-items" aria-label={`${t.code} items`}>
        {t.items.map((it) => (
          <li key={it.orderItemId}>
            <div className="gk-line">
              <Fig text={`${it.qty}×`} size="md" animate={false} className="gk-qty" />
              <div className="stack s4" style={{ minWidth: 0 }}>
                <span className="gk-nm">
                  {it.name}
                  {it.isCombo ? <span className="pill st-neutral gk-combo">Combo</span> : null}
                </span>
                {it.modifiers.length ? <span className="gk-mods">{it.modifiers.join(' · ')}</span> : null}
                {it.isCombo && it.comboParts.length ? (
                  <ul className="gk-parts" aria-label="In the combo">
                    {it.comboParts.map((p, i) => (
                      <li key={i}>{p}</li>
                    ))}
                  </ul>
                ) : null}
                {it.instructions ? (
                  <p className="gk-instr">
                    <span className="gk-instr-label">Note</span>
                    {it.instructions}
                  </p>
                ) : null}
              </div>
            </div>
          </li>
        ))}
      </ul>
      {t.note ? (
        <p className="gk-instr">
          <span className="gk-instr-label">Order note</span>
          {t.note}
        </p>
      ) : null}
      {other ? <span className="dv-sub">{other}</span> : null}
    </>
  );
}

function Ticket({
  t,
  now,
  selected,
  isNew,
  pending,
  problem,
  onSelect,
  onMove,
}: {
  t: KitchenTicket;
  now: number;
  selected: boolean;
  isNew: boolean;
  pending: boolean;
  problem: string | null;
  onSelect(): void;
  onMove(to: KitchenStatus): void;
}) {
  const late = t.kitchenStatus !== 'ready' && clockFor(t, now).late;
  const to = nextStatus(t.kitchenStatus);
  const label = moveLabel(t.kitchenStatus);
  const blocked = t.kitchenStatus === 'queued' && !canStart(t, now);
  return (
    <article
      className={`ticket gk-ticket${late ? ' late' : ''}${isNew ? ' new' : ''}`}
      aria-current={selected ? 'true' : undefined}
      aria-label={`${t.code}${t.customerFirstName ? `, ${t.customerFirstName}` : ''}`}
      data-ticket={t.shopOrderId}
      tabIndex={selected ? 0 : -1}
      onClick={onSelect}
      onFocus={onSelect}
    >
      <div className="gk-tk-h">
        <h3>
          <span className="ph-code">{t.code}</span>
          {t.customerFirstName ? <span className="nm">{t.customerFirstName}</span> : null}
        </h3>
        <Timer t={t} now={now} />
      </div>
      {t.speed === 'express' || t.scheduledFor ? (
        <div className="meta">
          {t.speed === 'express' ? <ExpressChip /> : null}
          {t.scheduledFor ? <span className="pill st-neutral">Scheduled</span> : null}
        </div>
      ) : null}
      <Items t={t} />
      {t.rider ? (
        <span className="dv-sub">
          <Icon name="person" size="sm" />
          Rider {t.rider.name}
        </span>
      ) : t.kitchenStatus === 'ready' ? (
        <span className="dv-sub">Waiting for a rider</span>
      ) : null}
      {problem ? (
        <div className="status-msg st-error" role="alert">
          {problem}
        </div>
      ) : null}
      {to && label ? (
        <div className="gk-actions">
          {canUndo(t.kitchenStatus) ? (
            <button
              type="button"
              className="btn btn-quiet"
              disabled={pending}
              onClick={(e) => {
                e.stopPropagation();
                onMove('queued');
              }}
            >
              Undo
            </button>
          ) : null}
          <button
            type="button"
            className={`btn${selected ? ' btn-primary' : ''} gk-go`}
            disabled={pending || blocked}
            aria-busy={pending || undefined}
            onClick={(e) => {
              e.stopPropagation();
              onMove(to);
            }}
          >
            {pending ? 'Saving…' : label}
          </button>
        </div>
      ) : null}
      {blocked ? <span className="dv-sub">{startsAtLabel(t)}</span> : null}
    </article>
  );
}

function LaterTicket({ t, now, pending, problem, onStart }: { t: KitchenTicket; now: number; pending: boolean; problem: string | null; onStart(): void }) {
  const ok = canStart(t, now);
  const hint = ok ? null : startsAtLabel(t);
  return (
    <div className="ticket gk-ticket">
      <div className="gk-tk-h">
        <h3>
          <span className="ph-code">{t.code}</span>
          {t.customerFirstName ? <span className="nm">{t.customerFirstName}</span> : null}
        </h3>
        <span className="gk-fires">
          <Icon name="clock" size="sm" />
          {firesAtLabel(t)}
        </span>
      </div>
      <Items t={t} />
      {problem ? (
        <div className="status-msg st-error" role="alert">
          {problem}
        </div>
      ) : null}
      <div className="gk-actions">
        <button type="button" className="btn" disabled={!ok || pending} aria-busy={pending || undefined} aria-describedby={hint ? `gk-hint-${t.shopOrderId}` : undefined} onClick={onStart}>
          {pending ? 'Saving…' : 'Start grilling'}
        </button>
      </div>
      {hint ? (
        <span id={`gk-hint-${t.shopOrderId}`} className="dv-sub">
          {hint}
        </span>
      ) : null}
    </div>
  );
}
